import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const decimalMoney = z.string().regex(/^-?(0|[1-9][0-9]{0,14})\.[0-9]{4}$/);
const money = decimalMoney.nullable();

const evidenceSchema = z.object({ attached: z.number().int().nonnegative(), missing: z.number().int().nonnegative().nullable(), attested: z.number().int().nonnegative() }).strict();
const subgroupSchema = z.object({ label: z.string(), rows: z.number().int().nonnegative(), amount: money, uncategorized: z.boolean() }).strict();
export const scheduleCLineSchema = z.object({
  tax_line: z.string(), label: z.string(), part: z.enum(['I', 'II']), row_count: z.number().int().nonnegative(), evidence: evidenceSchema,
  recorded: money, deductible: money, computed: z.boolean(), preparer_facts: z.array(z.string()).nullable(),
  subgroups: z.array(subgroupSchema), preparer_required: z.boolean(), preparer_reason: z.string().nullable(),
  uncategorized_count: z.number().int().nonnegative(), warning: z.string().nullable(),
}).strict();

const tieoutSchema = z.object({ form_type: z.string(), payer: z.string().nullable(), form_amount: money, matched_amount: decimalMoney, status: z.enum(['tied', 'off_by', 'unverified']), difference: money.optional() }).strict();
const scheduleCDataSchema = z.object({
  status: z.enum(['ready', 'empty', 'insufficient']), reason: z.string().nullable(), excluded_count: z.number().int().nonnegative().nullable(),
  taxonomy_version: z.string(), income_status: z.enum(['verified', 'unverified']), lines: z.array(scheduleCLineSchema),
  computed_line_28: scheduleCLineSchema, withheld_lines: z.array(z.number().int()), tieouts: z.array(tieoutSchema),
  year_lock: z.object({ locked: z.boolean(), filed_at: z.string().nullable(), state_version: z.number().int().positive(), revision: z.number().int().positive() }).strict(),
  as_of: z.string(),
}).strict();

export const scheduleCResponseSchema = z.object({ data: scheduleCDataSchema, meta: z.record(z.string(), z.unknown()).optional() }).strict();
export type ScheduleCData = z.infer<typeof scheduleCDataSchema>;

export function useScheduleC(grantId: string | null | undefined, taxYear: number | null | undefined) {
  return useQuery({
    queryKey: ['accountant-schedule-c', grantId, taxYear],
    enabled: Boolean(grantId && taxYear != null),
    queryFn: async () => parseEnvelope(scheduleCDataSchema, (await client.get(`/api/accountant/clients/${grantId}/years/${taxYear}/schedule-c`)).data).data,
  });
}
